import React, { useState, useEffect, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import StockTable from './StockTable';
import {
  Button,
  Dropdown,
  ExportFormatDialog,
  Input,
} from '../../../../common/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FilterComp from './FilterComp';
import { setBranchId } from '../../../auth/actions';
import { setFiltersVisibility } from '../../../../common/actions';
import { exportStocks } from '../actions';

const DEFAULT_PAGE = 0;
const DEFAULT_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 300;

const Stocks = ({
  setBranchId,
  auth: { branchList, branchId },
  commonState,
  setFiltersVisibility,
  exportStocks,
}) => {
  const [filters, setFilters] = useState({});
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef(null);
  const [productResetTrigger, setProductResetTrigger] = useState(false);
  const [supplierResetTrigger, setSupplierResetTrigger] = useState(false);
  const [categoryResetTrigger, setCategoryResetTrigger] = useState(false);
  const [selectedProduct, setselectedProduct] = useState(null);
  const [selectedSupplier, setselectedSupplier] = useState(null);
  const [selectedCategory, setselectedCategory] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('search') ?? '';
  const page = parseInt(searchParams.get('page'), 10);
  const limit = parseInt(searchParams.get('limit'), 10);
  const pageNum = Number.isFinite(page) && page >= 0 ? page : DEFAULT_PAGE;
  const limitNum = Number.isFinite(limit) && limit > 0 ? limit : DEFAULT_LIMIT;

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  useEffect(() => {
    const hasPage = searchParams.has('page');
    const hasLimit = searchParams.has('limit');
    if (!hasPage || !hasLimit) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!hasPage) next.set('page', String(DEFAULT_PAGE));
          if (!hasLimit) next.set('limit', String(DEFAULT_LIMIT));
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const onClickNewStock = () => {
    navigate('/inventory/stocks/new-stock');
  };

  const handleSearchChange = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value != null && value !== '') {
        next.set('search', String(value));
      } else {
        next.delete('search');
      }
      next.set('page', String(DEFAULT_PAGE));
      return next;
    });
  };

  const handleResetFilters = () => {
    setFilters(() => ({}));
    setselectedProduct(null);
    setselectedSupplier(null);
    setselectedCategory(null);
    setProductResetTrigger((prev) => !prev);
    setSupplierResetTrigger((prev) => !prev);
    setCategoryResetTrigger((prev) => !prev);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(DEFAULT_PAGE));
      return next;
    });
  };

  const handleChangeBranch = (value) => {
    setBranchId(value);
  };

  const handleExportClick = () => {
    setExportDialogOpen(true);
  };

  const handleExportConfirm = (format) => {
    exportStocks(filters, format);
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      if (!(key in prev)) return prev;
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    if (key === 'product') {
      setProductResetTrigger((prev) => !prev);
      setselectedProduct(null);
    } else if (key === 'supplier') {
      setSupplierResetTrigger((prev) => !prev);
      setselectedSupplier(null);
    } else if (key === 'category') {
      setCategoryResetTrigger((prev) => !prev);
      setselectedCategory(null);
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(DEFAULT_PAGE));
      return next;
    });
  };

  const handlePaginationChange = (newPage, newRowsPerPage) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const limitChanged =
        typeof newRowsPerPage === 'number' && newRowsPerPage !== limitNum;
      if (limitChanged) {
        next.set('limit', String(newRowsPerPage));
        next.set('page', String(DEFAULT_PAGE));
      } else if (typeof newPage === 'number') {
        next.set('page', String(newPage));
      }
      return next;
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>Stocks</h1>
          <p className={styles.breadcrumb}>Inventory / Stocks</p>
        </div>
        {branchList && branchList.length > 0 && branchId && (
          <div className={styles.branchSelectWrap}>
            <Dropdown
              label="Branch"
              id="branch-select"
              options={branchList.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              value={branchId}
              onChange={handleChangeBranch}
              aria-label="Select branch"
            />
          </div>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.chartMainDiv}>
          <div className={styles.topDiv}>
            <div className={styles.leftDiv}>
              <Button onClick={onClickNewStock}>New Stock</Button>
            </div>
            <div className={styles.rightDiv}>
              <Input
                placeholder="Search by code, name or barcode"
                value={search}
                onChange={handleSearchChange}
                className={styles.searchInput}
              />
              <Button
                variant="secondary"
                onClick={() => setFiltersVisibility(!commonState.isFiltersOpen)}
              >
                Filters
              </Button>
              <Button variant="secondary" onClick={handleExportClick}>
                Export
              </Button>
              <ExportFormatDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                onConfirm={handleExportConfirm}
              />
              <FilterComp
                filters={filters}
                setFilters={setFilters}
                supplierResetTrigger={supplierResetTrigger}
                productResetTrigger={productResetTrigger}
                categoryResetTrigger={categoryResetTrigger}
                selectedProduct={selectedProduct}
                setselectedProduct={setselectedProduct}
                selectedSupplier={selectedSupplier}
                setselectedSupplier={setselectedSupplier}
                selectedCategory={selectedCategory}
                setselectedCategory={setselectedCategory}
              />
            </div>
          </div>
          <div className={styles.addedFiltersDiv}>
            {Object.entries(filters).map(([key, value]) => (
              <span key={key} className={styles.filterTag}>
                {key === 'product'
                  ? `Product: ${value.name}`
                  : key === 'supplier'
                    ? `Supplier: ${value.companyname}`
                    : `Category: ${value.name}`}
                <button
                  type="button"
                  className={styles.removeFilterBtn}
                  onClick={() => handleRemoveFilter(key)}
                  aria-label={`Remove ${key} filter`}
                >
                  ×
                </button>
              </span>
            ))}
            {Object.keys(filters).length > 0 && (
              <button
                type="button"
                className={styles.resetFiltersBtn}
                onClick={handleResetFilters}
              >
                Reset filters
              </button>
            )}
          </div>
          <div className={styles.chartDiv}>
            <StockTable
              filterParams={filters}
              keyword={debouncedSearch}
              page={pageNum}
              rowsPerPage={limitNum}
              onPaginationChange={handlePaginationChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

Stocks.propTypes = {
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  commonState: PropTypes.object,
  setFiltersVisibility: PropTypes.func.isRequired,
  exportStocks: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  dashboard: state.dashboard,
  auth: state.authState,
  commonState: state.commonState,
});

export default connect(mapStateToProps, {
  setBranchId,
  setFiltersVisibility,
  exportStocks,
})(Stocks);
